'use client';

import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { logger } from '@/utils/loggerUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { authenticateUserInRestAPI } from '@vimond/services/rest-api';
import { confirmSignIn } from 'aws-amplify/auth';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import QRCode from 'react-qr-code';
import { z } from 'zod';

type Props = {
  onNotAuthorizedException: () => void;
  totpQRCode?: string;
};
export const SignInConfirmForm = ({
  onNotAuthorizedException,
  totpQRCode,
}: Props) => {
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<string>();

  const TotpFormSchema = z.object({
    code: z.string(),
  });

  type TotpFormSchemaType = z.infer<typeof TotpFormSchema>;
  const {
    formState: {
      errors: totpErrors,
      isDirty: totpIsDirty,
      isLoading: totpIsLoading,
      isSubmitting: totpIsSubmitting,
    },
    handleSubmit: handleTOTPSubmit,
    register: registerTOTP,
  } = useForm<TotpFormSchemaType>({
    resolver: zodResolver(TotpFormSchema),
    values: {
      code: '',
    },
  });

  const onTOTPSubmit: SubmitHandler<TotpFormSchemaType> = async (data) => {
    const { code } = data;
    await confirmSignIn({ challengeResponse: code })
      .then(async (res) => {
        if (res.isSignedIn) {
          await authenticateUserInRestAPI();
          setErrorMessage(undefined);
          // redirect to who is watching page using window.location.href so that the cookies are available for both server and client
          window.location.href = '/profiles/whoiswatching';
        }
      })
      .catch((err: Error) => {
        if (err.name === 'NotAuthorizedException') {
          // user is not authorized to perform this action
          setErrorMessage(t('auth.login-failed'));
          onNotAuthorizedException();
        } else if (err.name === 'CodeMismatchException') {
          setErrorMessage(t('auth.invalid-code-please-try-again'));
        } else {
          logger.error(err);
          setErrorMessage(t('auth.invalid-code-please-try-again'));
        }
      });
  };

  return (
    <div className="space-y-4 p-6 sm:p-8 md:space-y-6">
      <h1 className="text-center text-xl font-bold leading-tight tracking-tight md:text-2xl">
        {t('auth.multi-factor-authentication')}
      </h1>
      {totpQRCode && (
        <>
          <p className="text-text-secondary text-center text-sm">
            {t('auth.scan-the-qr-code-with-your-authenticator-app')}
          </p>
          <div className="flex justify-center">
            <QRCode value={totpQRCode} />
          </div>
        </>
      )}

      <form
        className="space-y-4 md:space-y-6"
        onSubmit={handleTOTPSubmit(onTOTPSubmit)}
      >
        <div>
          <Label htmlFor="code">
            {t('auth.enter-an-mfa-code-to-complete-sign-in')}
          </Label>
          <div className="mt-2">
            <Input
              id="code"
              type="text"
              {...registerTOTP('code')}
              placeholder={t('auth.code')}
              aria-describedby="email-error"
            />
          </div>
          <p
            id="email-error"
            className="text-red-500"
            aria-live="polite"
            aria-atomic="true"
          >
            {totpErrors.code?.message}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={totpIsLoading || totpIsSubmitting || !totpIsDirty}
          type="submit"
          className="w-full"
        >
          {t('sign-in')}
        </Button>
        {errorMessage && <p className="text-red-500">{errorMessage}</p>}
      </form>
    </div>
  );
};
